import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useState,
  useContext,
} from 'react';

interface IdMap {
  userIdJobId: `${string}-${string}`;
  userId: string;
}

interface ProviderState {
  'billable-alert': Record<IdMap['userIdJobId'], 'visible' | 'hidden'>;
  'recruiter-network-alert': Record<IdMap['userIdJobId'], 'visible' | 'hidden'>;
  'application-card-state': Record<IdMap['userId'], 'expanded' | 'collapsed'>;
  'certsy-verified-full-name-alert': Record<
    IdMap['userId'],
    'visible' | 'hidden'
  >;
  'freemium-urgent-hire': Record<IdMap['userIdJobId'], 'visible' | 'hidden'>;
  'freemium-hard-to-fill': Record<IdMap['userIdJobId'], 'visible' | 'hidden'>;
  'job-placement-expiry-timestamp': Record<IdMap['userIdJobId'], string>;
}
interface ContextValue {
  'billable-alert': {
    getBillableAlertStatus: () => 'visible' | 'hidden' | undefined;
    hideBillableAlert: () => void;
  };
  'recruiter-network-alert': {
    getRecruiterNetworkAlertStatus: () => 'visible' | 'hidden' | undefined;
    hideRecruiterNetworkAlert: () => void;
  };
  'application-card-state': {
    getApplicationCardState: () => 'expanded' | 'collapsed' | undefined;
    toggleApplicationCardState: () => void;
  };
  'certsy-verified-full-name-alert': {
    getCertsyVerifiedFullNameAlert: () => 'visible' | 'hidden' | undefined;
    hideCertsyVerifiedFullNameAlert: () => void;
  };
  'freemium-urgent-hire': {
    getFreemiumUrgentHireModalStatus: () => 'visible' | 'hidden' | undefined;
    hideFreemiumUrgentHireModal: () => void;
  };
  'freemium-hard-to-fill': {
    getFreemiumHardToFillModalStatus: () => 'visible' | 'hidden' | undefined;
    hideFreemiumHardToFillModal: () => void;
  };
  'job-placement-expiry-timestamp': {
    getJobPlacementExpiryTimestamp: () => string | undefined;
    hideJobPlacementExpiryTimestamp: () => void;
  };
}

const localStorageKey = 'cm-user-actions';
const createDefaultUserActions = (): ProviderState => ({
  'billable-alert': {},
  'recruiter-network-alert': {},
  'application-card-state': {},
  'certsy-verified-full-name-alert': {},
  'freemium-urgent-hire': {},
  'freemium-hard-to-fill': {},
  'job-placement-expiry-timestamp': {},
});
const syncUserActions = ({
  idMap,
  setValue,
  value,
}: {
  idMap: IdMap;
  value: ProviderState;
  setValue: Dispatch<SetStateAction<ProviderState>> | undefined;
}): void => {
  value['billable-alert'] = value['billable-alert'] || {};

  if (!value['billable-alert'][idMap.userIdJobId]) {
    value['billable-alert'][idMap.userIdJobId] = 'visible';
  }

  value['recruiter-network-alert'] = value['recruiter-network-alert'] || {};

  if (!value['recruiter-network-alert'][idMap.userIdJobId]) {
    value['recruiter-network-alert'][idMap.userIdJobId] = 'visible';
  }

  value['freemium-urgent-hire'] = value['freemium-urgent-hire'] || {};

  if (!value['freemium-urgent-hire'][idMap.userIdJobId]) {
    value['freemium-urgent-hire'][idMap.userIdJobId] = 'visible';
  }

  value['freemium-hard-to-fill'] = value['freemium-hard-to-fill'] || {};

  if (!value['freemium-hard-to-fill'][idMap.userIdJobId]) {
    value['freemium-hard-to-fill'][idMap.userIdJobId] = 'visible';
  }

  value['application-card-state'] = value['application-card-state'] || {};
  if (!value['application-card-state'][idMap.userId]) {
    value['application-card-state'][idMap.userId] = 'expanded';
  }

  value['certsy-verified-full-name-alert'] =
    value['certsy-verified-full-name-alert'] || {};

  if (!value['certsy-verified-full-name-alert'][idMap.userId]) {
    value['certsy-verified-full-name-alert'][idMap.userId] = 'visible';
  }

  value['job-placement-expiry-timestamp'] =
    value['job-placement-expiry-timestamp'] || {};

  if (!value['job-placement-expiry-timestamp'][idMap.userIdJobId]) {
    value['job-placement-expiry-timestamp'][idMap.userIdJobId] = new Date(
      0,
    ).toISOString();
    // NOTE: new Date(0) will trigger the component to show every time because it
    // returns 1970-01-01T00:00:00.000Z which is always earlier than today.
  }

  if (setValue) {
    setValue(value);
  }
  window.localStorage.setItem(localStorageKey, JSON.stringify(value));
};

const context = createContext<ContextValue | undefined>(undefined);

export const UserActionStatusProvider: FC<
  PropsWithChildren<{ userId: number; jobId: string }>
> = ({ children, userId, jobId }) => {
  const [idMap] = useState<IdMap>(() => ({
    userIdJobId: `${userId}-${jobId}`,
    userId: `${userId}`,
  }));
  const [userActions, setUserActionsState] = useState<ProviderState>(() => {
    const userActionsFromLocalStorage =
      window.localStorage.getItem(localStorageKey);

    if (!userActionsFromLocalStorage) {
      const value = createDefaultUserActions();
      syncUserActions({
        idMap,
        value,
        setValue: undefined,
      });
      return value;
    }

    try {
      const value = JSON.parse(userActionsFromLocalStorage);
      syncUserActions({
        idMap,
        value,
        setValue: undefined,
      });
      return value;
    } catch (err) {
      const value = createDefaultUserActions();
      syncUserActions({
        idMap,
        value,
        setValue: undefined,
      });
      return value;
    }
  });

  return (
    <context.Provider
      value={{
        'billable-alert': {
          getBillableAlertStatus: () => {
            if (idMap) {
              return userActions['billable-alert'][idMap.userIdJobId];
            }
          },
          hideBillableAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'billable-alert': {
                    ...userActions['billable-alert'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'recruiter-network-alert': {
          getRecruiterNetworkAlertStatus: () => {
            if (idMap) {
              return userActions['recruiter-network-alert'][idMap.userIdJobId];
            }
          },
          hideRecruiterNetworkAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'recruiter-network-alert': {
                    ...userActions['recruiter-network-alert'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'freemium-urgent-hire': {
          getFreemiumUrgentHireModalStatus: () => {
            if (idMap) {
              return userActions['freemium-hard-to-fill'][idMap.userIdJobId];
            }
          },
          hideFreemiumUrgentHireModal: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'freemium-hard-to-fill': {
                    ...userActions['freemium-hard-to-fill'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'freemium-hard-to-fill': {
          getFreemiumHardToFillModalStatus: () => {
            if (idMap) {
              return userActions['freemium-hard-to-fill'][idMap.userIdJobId];
            }
          },
          hideFreemiumHardToFillModal: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'freemium-hard-to-fill': {
                    ...userActions['freemium-hard-to-fill'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'application-card-state': {
          getApplicationCardState: () => {
            if (idMap) {
              return userActions['application-card-state'][idMap.userId];
            }
          },
          toggleApplicationCardState: () => {
            if (idMap) {
              const newCardState =
                userActions['application-card-state'][idMap.userId] ===
                'expanded'
                  ? 'collapsed'
                  : 'expanded';

              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'application-card-state': {
                    ...userActions['application-card-state'],
                    [idMap.userId]: newCardState,
                  },
                },
              });
            }
          },
        },
        'certsy-verified-full-name-alert': {
          getCertsyVerifiedFullNameAlert: () => {
            if (idMap) {
              return userActions['certsy-verified-full-name-alert'][
                idMap.userId
              ];
            }
          },
          hideCertsyVerifiedFullNameAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'certsy-verified-full-name-alert': {
                    ...userActions['certsy-verified-full-name-alert'],
                    [idMap.userId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'job-placement-expiry-timestamp': {
          getJobPlacementExpiryTimestamp: () => {
            if (idMap) {
              return userActions['job-placement-expiry-timestamp'][
                idMap.userIdJobId
              ];
            }
          },
          hideJobPlacementExpiryTimestamp: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'job-placement-expiry-timestamp': {
                    ...userActions['job-placement-expiry-timestamp'],
                    [idMap.userIdJobId]: new Date(
                      Date.now() + 2 * 24 * 60 * 60 * 1000,
                    ).toISOString(),
                  },
                },
              });
            }
          },
        },
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useUserActionStatus = <T extends keyof ContextValue>(
  actionType: T,
): ContextValue[T] => {
  const value = useContext(context);

  if (!value) {
    throw new Error(
      'useUserActionStatus must be used within a UserActionStatusProvider',
    );
  }

  return value[actionType];
};
